/**
 * Phase 9 (Phase 20: per layer) — one auto-send layer's settings as an
 * editable draft, pure: a campaign group's (its master config) or a
 * container's own layer on top. Config → draft, draft → the PUT body (only
 * what changed, so a save never rewrites what the person did not touch),
 * what blocks Save, and the stop-loss in one sentence. The rule is the
 * verdict editor's flat ALL/ANY list; a deeper tree set through the API is
 * shown read-only and never re-sent.
 * For Media Devoted | Created By Ciro Autiero
 */
import { type DropGenAutoSendConfig, stopLossOn } from '@trafficpulse/shared';

import { GATE_METRIC_OPTIONS } from '@/features/drop-generator/components/editor/verdicts/condition/gate-metric-options';

import type { DraftVerdictRow } from '../../../../drop-generator.types';
import type { DraftAutoSend } from '../../../../drop-generator-rule.types';
import { valueLabel } from '../../../editor/verdicts/condition/metric-meanings';
import { flatRuleToTree, parseFlatRule } from '../../../editor/state/satellites/verdict-draft';
import { isTagRow } from '../../../../drop-generator-rule.types';
import type { AutoSendPatch } from '../use-autosend-mutations';

export type LayerKind = 'group' | 'container';

/** The layer a dialog edits: a group (master config) or a container's own. */
export interface AutoSendLayer {
  id: string;
  kind: LayerKind;
  name: string;
  /** The campaign group's name (its own for a group). */
  groupName: string;
  config: DropGenAutoSendConfig;
}

export type AutoSendDraft = DraftAutoSend;

/** The stop-loss most clients start from: delivered under 85 % OR bots over
 *  15 % — either one halts (ANY), as the sentence says. */
export const STARTER_JOIN = 'OR' as const;
export const STARTER_ROWS: DraftVerdictRow[] = [
  { metric: 'deliveredRate', op: '<', value: 0.85 },
  { metric: 'botRate', op: '>', value: 0.15 },
];

export function layerToDraft(c: DropGenAutoSendConfig): AutoSendDraft {
  const rule = parseFlatRule(c.stopLossJson);
  return {
    enabled: c.autoSend,
    dispatchMode: c.dispatchMode ?? 'DUE_TIME',
    shadow: c.shadow,
    noCap: c.dailySmsCap === null,
    cap: c.dailySmsCap ?? 20_000,
    stopLossEnabled: c.stopLossEnabled ?? c.stopLossJson !== null,
    join: rule.join,
    rows: rule.rows,
    advanced: rule.advanced,
    minSample: c.stopLossMinSampleSent,
    delayMin: c.stopLossDelayMin,
  };
}

export function draftToPatch(c: DropGenAutoSendConfig, d: AutoSendDraft): AutoSendPatch {
  const base = layerToDraft(c);
  const patch: AutoSendPatch = {};
  if ((d.dispatchMode ?? 'DUE_TIME') !== base.dispatchMode) patch.dispatchMode = d.dispatchMode ?? 'DUE_TIME';
  if (d.enabled !== base.enabled) patch.enabled = d.enabled;
  if (d.shadow !== base.shadow) patch.shadow = d.shadow;
  const cap = d.noCap ? null : d.cap;
  if (cap !== c.dailySmsCap) patch.dailySmsCap = cap;
  const rule = (x: AutoSendDraft) => JSON.stringify(flatRuleToTree(x));
  if (!d.advanced && rule(d) !== rule(base)) patch.stopLossJson = d.rows.length === 0 ? null : flatRuleToTree(d);
  if (d.stopLossEnabled !== base.stopLossEnabled) patch.stopLossEnabled = d.stopLossEnabled;
  if (d.minSample !== base.minSample) patch.stopLossMinSampleSent = d.minSample;
  if (d.delayMin !== base.delayMin) patch.stopLossDelayMin = d.delayMin;
  return patch;
}

/** Why Save is off — full sentences; empty = it may save. The stop-loss is
 *  optional on either layer (22/10/2026). */
export function draftProblems(d: AutoSendDraft): string[] {
  const out: string[] = [];
  if (d.stopLossEnabled && !d.advanced && d.rows.length === 0)
    out.push('Add a stop-loss condition — or switch the stop-loss off.');
  if (!d.noCap && !(d.cap >= 1)) out.push('The daily cap needs at least 1 SMS — or switch on No limit.');
  if (!(d.minSample >= 0)) out.push('The minimum sample cannot be negative (0 = off).');
  if (!(d.delayMin >= 0 && d.delayMin <= 1440)) out.push('The settle time is 0 to 1,440 minutes.');
  return out;
}

const LABEL = new Map<string, string>(GATE_METRIC_OPTIONS.map((o) => [o.value, o.label]));

/** "halts for today when Delivered Rate < 85 % or Bot Rate > 15 %, from 500 settled SMS". */
export function describeStopLoss(c: DropGenAutoSendConfig): string {
  // 23/10/2026 — the stop-loss has its own switch; off keeps the rule.
  if (c.stopLossJson !== null && !stopLossOn(c)) return 'off — its rule is kept; only the daily cap and Kill stop the day';
  const rule = parseFlatRule(c.stopLossJson);
  // 20/10/2026 — the minimum sample is optional: named only when one is set.
  const from = c.stopLossMinSampleSent > 0 ? `, from ${c.stopLossMinSampleSent.toLocaleString('en-US')} settled SMS` : '';
  if (rule.advanced) return `halts for today when its rule (set through the API) matches${from}`;
  if (rule.rows.length === 0) return 'none set — only the daily cap and Kill stop the day';
  const word = rule.join === 'AND' ? ' and ' : ' or ';
  // A tag row never reaches the stop-loss (refused on save); said as it is if one ever did.
  const parts = rule.rows.map((r) =>
    isTagRow(r)
      ? `${r.negate ? 'not ' : ''}tagged "${r.tag}"`
      : `${LABEL.get(r.metric) ?? r.metric} ${r.op} ${valueLabel(r.metric, r.value)}`,
  );
  return `halts for today when ${parts.join(word)}${from}`;
}
